// Utils
import type { GetStaticProps, NextPage } from 'next'
import Head from 'next/head'
import { serialize } from 'next-mdx-remote/serialize'
import { getPlaiceholder } from 'plaiceholder'
import tw, { css } from 'twin.macro'

// Types
import type { TimelineItem } from '../components/molecules/timeline/TimelineItem'
// Components
import {
  HomeBlog,
  HomeIntro,
  HomeProjects,
  HomeSocials,
  HomeTimeline,
} from '../components/organisms/Home'
import TheContactBanner from '../components/organisms/TheContactBanner'
// --- COMPONENTE DO CHATBOT ---
import TheChatBot from '../components/TheChatBot'
// Content
import allProjects from '../content/projects'
import { homeSocials } from '../content/socials'
import { timeline as allTimeline } from '../content/timeline'
import type { Post } from '../types/hashnode'
import type { Project } from '../types/project'
import type { SocialAccount } from '../types/social-account'
import type { TimelineItem as ITimelineItem } from '../types/timeline'
// Utils Logic
import { getUserPosts } from '../utils/hashnode'

interface Props {
  projects: Project[]
  posts: Post[]
  socials: SocialAccount[]
  timeline: TimelineItem[]
}

const sectionStyles = css`
  ${tw`my-19 first:mt-0 mx-auto max-w-2xl`}
`

const Home: NextPage<Props> = ({ projects, timeline, socials, posts }) => {
  return (
    <div tw="container mx-auto">
      <Head>
        <title>Jair Rodrigues</title>

        <meta
          name="description"
          content="Olá! Meu nome é Jair Rodrigues, sou engenheiro de computação e pesquisador focado em IA e soluções de alta performance."
        />

        <meta name="og:title" content="Jair Rodrigues" />
        <meta name="og:site_name" content="Jair Rodrigues" />
        <meta
          name="og:description"
          content="Desenvolvedor focado em Backend, Python, Data Science e Automação."
        />

        <meta name="twitter:title" content="Jair Rodrigues" />
        <meta
          name="twitter:description"
          content="Engenheiro de Computação na UFRPE explorando o mundo da tecnologia e IA."
        />
      </Head>

      {/* Seções Principais */}
      <HomeIntro id="intro" css={[sectionStyles]} />

      <HomeProjects
        id="projects"
        css={[sectionStyles]}
        projects={projects.slice(0, 3)}
      />

      <HomeBlog id="blog" css={[sectionStyles]} posts={posts} />

      <HomeTimeline id="timeline" css={[sectionStyles]} timeline={timeline} />

      <HomeSocials id="socials" css={[sectionStyles]} socials={socials} />

      {/* Banner de Contato antes do ChatBot */}
      <TheContactBanner tw="my-19" />

      {/* --- O CHATBOT APARECE AQUI --- */}
      {/* Como ele tem posição 'fixed' no CSS, ele ficará flutuando no canto da tela */}
      <TheChatBot />
    </div>
  )
}

export default Home

// --- DATA FETCHING ---
export const getStaticProps: GetStaticProps<Props> = async () => {
  const projects = allProjects.filter(
    (p) => p.display === undefined || p.display === true,
  )

  const posts = (await getUserPosts('devjair'))
    .slice(0, 3)
    .map(async (post) => {
      const plaiceholdrer =
        post.coverImage && post.coverImage.url
          ? await getPlaiceholder(post.coverImage.url)
          : null

      return {
        ...post,
        coverImage: {
          ...post.coverImage,
          base64: plaiceholdrer?.base64 ?? null,
          blurhash: plaiceholdrer?.blurhash ?? null,
        },
      }
    })

  const timeline = allTimeline
    .filter((t) => t.homepage)
    .map(async (t: ITimelineItem): Promise<TimelineItem> => {
      const _t: TimelineItem = Object.assign<
        ITimelineItem,
        Partial<TimelineItem>
      >(t, {})

      t.description && (_t.description = await serialize(t.description))
      _t.title = await serialize(t.title)

      return _t
    })

  return {
    props: {
      projects,
      posts: await Promise.all(posts),
      timeline: await Promise.all(timeline),
      socials: homeSocials,
    },
  }
}
